/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

import { css, jsx } from '@compiled/react';
import ReactDOM from 'react-dom';

import {
	findClosestSnap,
	generateDefaultGuidelines,
	getGuidelineSnaps,
	getGuidelinesWithHighlights,
} from '@atlaskit/editor-common/guideline';
import type { GuidelineConfig } from '@atlaskit/editor-common/guideline';
import type { Dimensions, HandleResize, Position, Snap } from '@atlaskit/editor-common/resizer';
import { ResizerNext } from '@atlaskit/editor-common/resizer';
import type { ExtractInjectionAPI, UiComponentFactoryParams } from '@atlaskit/editor-common/types';
import { NodeSelection } from '@atlaskit/editor-prosemirror/state';
import { findDomRefAtPos, type ContentNodeWithPos } from '@atlaskit/editor-prosemirror/utils';
import {
	akEditorDefaultLayoutWidth,
	akEditorFullWidthLayoutWidth,
	akEditorGutterPaddingDynamic,
	breakoutWideScaleRatio,
} from '@atlaskit/editor-shared-styles';

import type { EditorPluginNativeEmbedsPlugin } from '../nativeEmbedsPluginType';
import { getAlignment } from '../pm-plugins/commands/alignment';
import { createUpdateWidthCommand, getWidth } from '../pm-plugins/commands/resize';
import { NATIVE_EMBED_PLUGIN_IS_RESIZING_KEY } from '../pm-plugins/main';
import { getSelectedNativeEmbedExtension } from '../pm-plugins/utils/getSelectedNativeEmbedExtension';

export const resizerNextTestId = 'nativeEmbed.resizerNext.testid';

type ResizableNativeEmbedProps = UiComponentFactoryParams & {
	pluginInjectionApi: ExtractInjectionAPI<EditorPluginNativeEmbedsPlugin> | undefined;
};

interface NodeRect {
	height: number;
	left: number;
	top: number;
	width: number;
}

// ---------------------------------------------------------------------------
// Styles (compiled CSS)
// ---------------------------------------------------------------------------

const resizerWrapperStyles = css({
	position: 'absolute',
	pointerEvents: 'auto',
});

const resizerPlaceholderStyles = css({
	width: '100%',
	pointerEvents: 'none',
});

// TODO: CNS-24595 - use the constants file.
const MIN_WIDTH = 200;
const SNAP_GAP = 8;

export const ResizableNativeEmbed = (props: ResizableNativeEmbedProps) => {
	const { pluginInjectionApi, disabled, editorView: view } = props;
	const disableHandles = !!disabled;

	const [selectedNativeEmbed, setSelectedNativeEmbed] = useState<ContentNodeWithPos | null>(null);

	// -----------------------------------------------------------------------
	// Reactive width plugin subscription
	// -----------------------------------------------------------------------

	const [lineLength, setLineLength] = useState(() => {
		return (
			pluginInjectionApi?.width.sharedState.currentState()?.lineLength || akEditorDefaultLayoutWidth
		);
	});
	const [containerWidth, setContainerWidth] = useState(() => {
		return (
			pluginInjectionApi?.width.sharedState.currentState()?.width || akEditorDefaultLayoutWidth
		);
	});

	useEffect(() => {
		const unsubscribe = pluginInjectionApi?.width.sharedState.onChange(({ nextSharedState }) => {
			if (nextSharedState) {
				setLineLength(nextSharedState.lineLength || akEditorDefaultLayoutWidth);
				setContainerWidth(nextSharedState.width || akEditorDefaultLayoutWidth);
			}
		});
		return () => {
			unsubscribe?.();
		};
	}, [pluginInjectionApi]);

	// -----------------------------------------------------------------------
	// Reactive native embed selection subscription
	// -----------------------------------------------------------------------

	const [selectedEmbedPos, setSelectedEmbedPos] = useState<number | null>(() => {
		return (
			pluginInjectionApi?.editorPluginNativeEmbeds?.sharedState?.currentState()?.selectedEmbedPos ??
			null
		);
	});

	// Guard to prevent shared state updates during resize (PM transactions
	// dispatched during drag would trigger re-renders and position jumps)
	const isResizingRef = useRef(false);

	useEffect(() => {
		const initial = pluginInjectionApi?.editorPluginNativeEmbeds?.sharedState?.currentState();
		setSelectedEmbedPos(initial?.selectedEmbedPos ?? null);

		const unsub = pluginInjectionApi?.editorPluginNativeEmbeds?.sharedState?.onChange?.(
			({ nextSharedState }) => {
				if (isResizingRef.current) {
					return;
				}
				setSelectedEmbedPos(nextSharedState?.selectedEmbedPos ?? null);
			},
		);
		return () => {
			unsub?.();
		};
	}, [pluginInjectionApi]);

	// TODO: CNS-24595 - Rerender isn't consistently being set / set
	useEffect(() => {
		const selected = view && getSelectedNativeEmbedExtension(view.state);
		if (selected) {
			setSelectedNativeEmbed(selected);
		} else if (selectedEmbedPos !== null) {
			setSelectedNativeEmbed(null);
		}
	}, [selectedEmbedPos, view, view?.state]);

	// -----------------------------------------------------------------------
	// Derive current width from node attributes (layout-aware)
	// -----------------------------------------------------------------------

	const currentWidth = useMemo(() => {
		if (!selectedNativeEmbed || !view) {
			return akEditorDefaultLayoutWidth;
		}
		const node = selectedNativeEmbed.node;
		if (!node) {
			return akEditorDefaultLayoutWidth;
		}
		const storedWidth = getWidth(node);
		if (storedWidth) {
			return storedWidth;
		}

		// TODO: CNS-24595 - review this logic.
		const layout = node.attrs.layout || 'default';
		const paramWidth = node.attrs.parameters?.width;

		// Always use the persisted parameters.width when available — this is
		// the committed pixel width from the last resize operation.
		if (typeof paramWidth === 'number' && paramWidth > 0) {
			return paramWidth;
		}

		// Fallback when no parameters.width is set (first render / never resized)
		if (layout === 'wide') {
			return Math.ceil(lineLength * breakoutWideScaleRatio);
		}
		if (layout === 'full-width') {
			return akEditorFullWidthLayoutWidth;
		}
		return lineLength;
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [selectedNativeEmbed, lineLength]);

	// -----------------------------------------------------------------------
	// Resize state
	// -----------------------------------------------------------------------

	const [isResizing, setIsResizing] = useState(false);
	const [resizeWidth, setResizeWidth] = useState<number>(akEditorDefaultLayoutWidth);
	const [snaps, setSnaps] = useState<Snap>({});

	// Sync resizeWidth with currentWidth when not actively resizing.
	// Use a ref to track the last committed width so we don't snap back
	// to a stale currentWidth while the async extension API update propagates.
	const committedWidthRef = useRef<number | null>(null);

	useEffect(() => {
		if (!isResizing) {
			// If we have a recently committed width, only sync once currentWidth
			// matches it (meaning the async update has propagated), or if the
			// selection changed entirely.
			if (committedWidthRef.current !== null) {
				if (currentWidth === committedWidthRef.current) {
					// Extension API update has propagated — clear the guard
					committedWidthRef.current = null;
				}
				// Don't override resizeWidth until propagation completes
				return;
			}
			setResizeWidth(currentWidth);
		}
	}, [currentWidth, isResizing]);

	const [embedDomElement, setEmbedDomElement] = useState<HTMLElement | null>(null);
	const [portalTarget, setPortalTarget] = useState<HTMLElement | null>(null);
	const [nodeRect, setNodeRect] = useState<NodeRect | null>(null);

	// Measure the embed DOM position for overlay positioning.
	// Only runs when selection changes or resize finishes — NOT during drag.
	// During drag, the overlay position is computed live in the render body.
	useLayoutEffect(() => {
		// Skip during active resize to avoid stale measurements from transform styles
		if (isResizing) {
			return;
		}

		if (!selectedNativeEmbed || !view) {
			setNodeRect(null);
			setPortalTarget(null);
			return;
		}

		try {
			const domNode = findDomRefAtPos(selectedNativeEmbed.pos, view.domAtPos.bind(view));
			const element =
				domNode instanceof HTMLElement ? domNode : (domNode.parentElement as HTMLElement | null);
			if (!element) {
				setNodeRect(null);
				setPortalTarget(null);
				return;
			}

			// Use .extension-container when present (matches floating toolbar anchor logic)
			const measureEl =
				(element.querySelector('.extension-container') as HTMLElement | null) ??
				(element.closest('.extension-container') as HTMLElement | null) ??
				element;

			// Use scroll container first, then content area
			const container =
				(view.dom.closest('.fabric-editor-popup-scroll-parent') as HTMLElement | null) ||
				(view.dom.closest('.ak-editor-content-area') as HTMLElement | null) ||
				view.dom.parentElement;
			if (!container) {
				setNodeRect(null);
				setPortalTarget(null);
				return;
			}

			setPortalTarget(container);

			const containerRect = container.getBoundingClientRect();
			const nodeRectRaw = measureEl.getBoundingClientRect();

			setNodeRect({
				top: nodeRectRaw.top - containerRect.top + container.scrollTop,
				left: nodeRectRaw.left - containerRect.left + container.scrollLeft,
				width: nodeRectRaw.width,
				height: nodeRectRaw.height,
			});
		} catch {
			setNodeRect(null);
			setPortalTarget(null);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [selectedNativeEmbed, view, isResizing]);

	const maxWidth = useMemo(() => {
		const padding = akEditorGutterPaddingDynamic();
		return containerWidth - padding * 2;
	}, [containerWidth]);

	// -----------------------------------------------------------------------
	// Guidelines
	// -----------------------------------------------------------------------

	const defaultGuidelines = useMemo(() => {
		return generateDefaultGuidelines(lineLength, containerWidth, false);
	}, [lineLength, containerWidth]);

	// Mutable ref so handleResize can read current guidelines without re-creating
	const guidelinesRef = useMemo(() => ({ current: defaultGuidelines }), [defaultGuidelines]);

	const isGuidelineEnabled = useMemo(() => {
		return !!pluginInjectionApi?.guideline;
	}, [pluginInjectionApi]);

	// TODO: CNS-24595 - This logic needs adjusting on alignment changes.
	const updateActiveGuidelines = useCallback(
		(width: number, guidelines: GuidelineConfig[]) => {
			// console.log("[debug] - updateActiveGuidelines width, guidelines", width, guidelines);
			const guidelineSnaps = getGuidelineSnaps(guidelines, lineLength, 'center');
			if (!guidelineSnaps.snaps.x) {
				return;
			}
			const { gap, keys: activeGuidelineKeys } = findClosestSnap(
				width,
				guidelineSnaps.snaps.x,
				guidelineSnaps.guidelineReference,
				SNAP_GAP,
			);
			// console.log("[debug] - updateActiveGuidelines gap, activeGuidelineKeys", gap, activeGuidelineKeys);

			const nextGuideLines = getGuidelinesWithHighlights(
				gap,
				SNAP_GAP,
				activeGuidelineKeys,
				guidelines,
			);
			// console.log("[debug] - updateActiveGuidelines nextGuideLines", nextGuideLines);
			view &&
				pluginInjectionApi?.guideline?.actions?.displayGuideline(view)({
					guidelines: nextGuideLines,
				});
		},
		[pluginInjectionApi, view, lineLength],
	);

	// -----------------------------------------------------------------------
	// Resize handlers (must be before early return — hooks must run unconditionally)
	// -----------------------------------------------------------------------

	const handleResizeStart = useCallback(() => {
		setIsResizing(true);
		isResizingRef.current = true;

		// Grab a reference to the actual embed DOM element so we can set its
		// width directly during drag for smooth live preview.
		if (selectedNativeEmbed && view) {
			try {
				const domNode = findDomRefAtPos(selectedNativeEmbed.pos, view.domAtPos.bind(view));
				const element =
					domNode instanceof HTMLElement ? domNode : (domNode.parentElement as HTMLElement | null);
				if (element) {
					const container =
						(element.querySelector('.extension-container') as HTMLElement | null) ??
						(element.closest('.extension-container') as HTMLElement | null) ??
						element;
					setEmbedDomElement(container);

					// Sync resizeWidth to the actual DOM width so the first drag
					// starts from where the content visually is, not from the
					// potentially stale currentWidth (e.g. lineLength fallback).
					const actualWidth = container.getBoundingClientRect().width;
					if (actualWidth > 0) {
						setResizeWidth(Math.round(actualWidth));
					}
				}
			} catch {
				// ignore – we'll just not do live width updates
			}

			const { state, dispatch } = view;
			const tr = state.tr;
			tr.setMeta(NATIVE_EMBED_PLUGIN_IS_RESIZING_KEY, true);
			tr.setMeta('is-resizer-resizing', true);
			tr.setMeta('scrollIntoView', false);
			tr.setSelection(NodeSelection.create(state.doc, selectedNativeEmbed.pos));
			dispatch(tr);
		}
	}, [view, selectedNativeEmbed]);

	const handleResize: HandleResize = useCallback(
		(size: Position & Dimensions, delta: Dimensions) => {
			const newWidth = Math.round(size.width + delta.width);
			const clampedWidth = Math.max(MIN_WIDTH, Math.min(newWidth, maxWidth));

			// Snap to the nearest guideline if within SNAP_GAP
			let snappedWidth = clampedWidth;
			const guidelineSnaps = getGuidelineSnaps(guidelinesRef.current, lineLength, 'center');
			if (guidelineSnaps.snaps.x && guidelineSnaps.snaps.x.length > 0) {
				const { gap } = findClosestSnap(
					clampedWidth,
					guidelineSnaps.snaps.x,
					guidelineSnaps.guidelineReference,
					SNAP_GAP,
				);
				if (gap !== undefined && Math.abs(gap) <= SNAP_GAP) {
					snappedWidth = clampedWidth + gap;
				}
			}

			setResizeWidth(snappedWidth);

			// Update active guidelines
			if (isGuidelineEnabled) {
				updateActiveGuidelines(snappedWidth, guidelinesRef.current);
				setSnaps({
					x: guidelineSnaps.snaps.x || [],
				});
			}

			// Directly set the embed DOM element's inline styles for smooth live preview.
			// Center the content using transform so both handles move symmetrically.
			if (embedDomElement) {
				embedDomElement.style.transition = 'none';
				embedDomElement.style.width = `${snappedWidth}px`;
				embedDomElement.style.minWidth = `${snappedWidth}px`;
				embedDomElement.style.maxWidth = `${snappedWidth}px`;
			}
		},
		[
			maxWidth,
			lineLength,
			guidelinesRef,
			isGuidelineEnabled,
			updateActiveGuidelines,
			embedDomElement,
		],
	);

	const handleResizeStop: HandleResize = useCallback(
		(_size: Position & Dimensions, _delta: Dimensions) => {
			// Use the current resizeWidth which already has snapping applied
			// from handleResize. This ensures the committed width matches
			// exactly where the handles are visually positioned.
			const committedWidth = resizeWidth;

			setIsResizing(false);
			setResizeWidth(committedWidth);
			committedWidthRef.current = committedWidth;

			// Keep width inline styles so content stays at the committed width.
			// Clear transform/margin centering so future measurements aren't affected.
			// They'll all be cleared when the embed is deselected.
			if (embedDomElement) {
				embedDomElement.style.transition = '';
				embedDomElement.style.width = `${committedWidth}px`;
				embedDomElement.style.minWidth = `${committedWidth}px`;
				embedDomElement.style.maxWidth = `${committedWidth}px`;
			}
			// Clear guidelines
			view && pluginInjectionApi?.guideline?.actions?.displayGuideline(view)({ guidelines: [] });

			if (!selectedNativeEmbed || !view) {
				isResizingRef.current = false;
				return;
			}

			// Read the node fresh from the current state (not the stale closure)
			const currentNode = selectedNativeEmbed.node;
			if (!currentNode) {
				isResizingRef.current = false;
				return;
			}

			// Commit width and layout to the document
			createUpdateWidthCommand(
				pluginInjectionApi,
				committedWidth,
				selectedNativeEmbed.node,
			)(view.state, view.dispatch);

			// Clear resizing plugin state
			const { state, dispatch } = view;
			const tr = state.tr;
			tr.setMeta(NATIVE_EMBED_PLUGIN_IS_RESIZING_KEY, false);
			tr.setMeta('is-resizer-resizing', false);
			tr.setMeta('scrollIntoView', false);
			dispatch(tr);

			// Release the resize guard after a frame so that the shared state
			// subscription ignores the transaction-triggered updates above.
			// Without this, the subscription would fire immediately with stale
			// DOM measurements, causing the handles to jump back.
			requestAnimationFrame(() => {
				isResizingRef.current = false;
			});
		},
		[selectedNativeEmbed, pluginInjectionApi, view, embedDomElement, resizeWidth],
	);

	// -----------------------------------------------------------------------
	// Early return — nothing to render (after all hooks)
	// -----------------------------------------------------------------------

	if (selectedEmbedPos === null || !view || !nodeRect || !portalTarget || !selectedNativeEmbed) {
		return null;
	}

	// TODO: CNS-24595 - we need to rerender when alignment changes.
	const currentAlignment = getAlignment(selectedNativeEmbed.node);
	const isRightTypeAligned = currentAlignment === 'right' || currentAlignment === 'wrap-right';
	const isLeftTypeAligned = currentAlignment === 'left' || currentAlignment === 'wrap-left';

	const enableRightHandle =
		!disableHandles && currentAlignment !== 'right' && currentAlignment !== 'wrap-right';
	const enableLeftHandle =
		!disableHandles && currentAlignment !== 'left' && currentAlignment !== 'wrap-left';

	// -----------------------------------------------------------------------
	// Overlay positioning — center over the embed
	// -----------------------------------------------------------------------

	let centerX: number;
	if (isResizing && embedDomElement) {
		// During resize the embed DOM element is CSS-centered via transform.
		// Read its actual center from the DOM.
		const portalRect = portalTarget.getBoundingClientRect();
		const embedRect = embedDomElement.getBoundingClientRect();
		centerX = embedRect.left - portalRect.left + portalTarget.scrollLeft + embedRect.width / 2;
	} else {
		// When not resizing, use the nodeRect center
		centerX = nodeRect.left + nodeRect.width / 2;
	}

	// TODO: CNS-24595 - need to calculate this correctly.
	const layoutWidth = akEditorDefaultLayoutWidth;

	let wrapperLeft: number;
	if (isRightTypeAligned) {
		wrapperLeft = (containerWidth + layoutWidth) / 2 - resizeWidth;
		// console.log("[debug] - wrapperLeft (right aligned)", wrapperLeft, "containerWidth", containerWidth, "resizeWidth", resizeWidth);
	} else if (isLeftTypeAligned) {
		wrapperLeft = (containerWidth - layoutWidth) / 2;
		// console.log("[debug] - wrapperLeft (left aligned)", wrapperLeft, "resizeWidth", resizeWidth, "layoutWidth", layoutWidth, "containerWidth", containerWidth);
	} else {
		wrapperLeft = centerX - resizeWidth / 2;
	}

	const overlay = (
		<div data-testid="native-embed-resizer">
			<div
				css={resizerWrapperStyles}
				//eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- dynamic positioning from DOM
				style={{
					top: nodeRect.top,
					left: wrapperLeft,
					width: resizeWidth,
				}}
				data-testid="native-embed-resizer-wrapper"
			>
				<ResizerNext
					enable={{
						left: enableLeftHandle,
						right: enableRightHandle,
					}}
					width={resizeWidth}
					minWidth={MIN_WIDTH}
					maxWidth={maxWidth}
					handleResizeStart={handleResizeStart}
					handleResize={handleResize}
					handleResizeStop={handleResizeStop}
					snapGap={SNAP_GAP}
					snap={snaps}
					resizeRatio={currentAlignment === 'center' ? 2 : 1}
					isHandleVisible={!disableHandles}
					handleHighlight="full-height"
					handlePositioning="adjacent"
					data-testid={resizerNextTestId}
				>
					<div
						css={resizerPlaceholderStyles}
						// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- dynamic height from DOM
						style={{ height: nodeRect.height || 600 }} // TODO: CNS-24595 - 600 is a hack, it can lose the select in cases and just falls back to this. We can do better
					/>
				</ResizerNext>
			</div>
		</div>
	);

	return ReactDOM.createPortal(overlay, portalTarget);
};
